import http from "./http";
import type {
  AccessControlCatalogModule,
  AccessRole,
  AccessRoleDetail,
  AccessRoleEffectivePreview,
  AccessRoleSummaryFilters,
  AssignClientAccessPayload,
  AssignInternalUserAccessPayload,
  ActorModuleOverridesResult,
  ActorPermissionOverridesResult,
  ActorRoleAssignmentResult,
  ClientAccessPreview,
  ClientDirectoryResponse,
  DuplicateAccessRolePayload,
  InternalUserDirectoryResponse,
  InternalUserAccessPreview,
  RoleModuleDefaultInput,
  RolePermissionDefaultInput,
  StoreAccessRolePayload,
  UpdateAccessRolePayload,
} from "@/types/accessControl";

export const getAccessControlCatalog = async () => {
  const response = await http.get<{ modules: AccessControlCatalogModule[] }>(
    "/api/access-control/catalog"
  );
  return response.data;
};

export const getAccessRoles = async (filters: AccessRoleSummaryFilters = {}) => {
  const params: Record<string, string> = {};

  if (filters.actor_type) {
    params.actor_type = filters.actor_type;
  }
  if (typeof filters.is_active === "boolean") {
    params.is_active = String(filters.is_active);
  }
  if (typeof filters.is_system === "boolean") {
    params.is_system = String(filters.is_system);
  }

  const response = await http.get<{ roles: AccessRole[] }>(
    "/api/access-control/roles",
    { params }
  );
  return response.data;
};

export const createAccessRole = async (payload: StoreAccessRolePayload) => {
  const response = await http.post<{ role: AccessRole }>(
    "/api/access-control/roles",
    payload
  );
  return response.data;
};

export const getAccessRoleDetail = async (roleId: number | string) => {
  const response = await http.get<{ role_detail: AccessRoleDetail }>(
    `/api/access-control/roles/${roleId}`
  );
  return response.data;
};

export const updateAccessRole = async (
  roleId: number | string,
  payload: UpdateAccessRolePayload
) => {
  const response = await http.patch<{ role: AccessRole }>(
    `/api/access-control/roles/${roleId}`,
    payload
  );
  return response.data;
};

export const duplicateAccessRole = async (
  roleId: number | string,
  payload: DuplicateAccessRolePayload
) => {
  const response = await http.post<{ role_detail: AccessRoleDetail }>(
    `/api/access-control/roles/${roleId}/duplicate`,
    payload
  );
  return response.data;
};

export const archiveAccessRole = async (roleId: number | string) => {
  const response = await http.post<{ role: AccessRole }>(
    `/api/access-control/roles/${roleId}/archive`
  );
  return response.data;
};

export const getAccessRoleDefaults = async (roleId: number | string) => {
  const response = await http.get<{ role_detail: AccessRoleDetail }>(
    `/api/access-control/roles/${roleId}/defaults`
  );
  return response.data;
};

export const updateAccessRoleModuleDefaults = async (
  roleId: number | string,
  items: RoleModuleDefaultInput[]
) => {
  const response = await http.put<{ role_detail: AccessRoleDetail }>(
    `/api/access-control/roles/${roleId}/module-defaults`,
    { items }
  );
  return response.data;
};

export const updateAccessRolePermissionDefaults = async (
  roleId: number | string,
  items: RolePermissionDefaultInput[]
) => {
  const response = await http.put<{ role_detail: AccessRoleDetail }>(
    `/api/access-control/roles/${roleId}/permission-defaults`,
    { items }
  );
  return response.data;
};

export const getAccessRoleEffectivePreview = async (
  roleId: number | string
) => {
  const response = await http.get<AccessRoleEffectivePreview>(
    `/api/access-control/roles/${roleId}/effective-preview`
  );
  return response.data;
};

export const getInternalUserAccessPreview = async (
  userId: number | string
) => {
  const response = await http.get<InternalUserAccessPreview>(
    `/api/internal-users/${userId}/access-control`
  );
  return response.data;
};

export const getClientAccessPreview = async (clientId: number | string) => {
  const response = await http.get<ClientAccessPreview>(
    `/api/clients/${clientId}/access-control`
  );
  return response.data;
};

export const getAccessControlUsers = async (params: {
  search?: string;
  status?: "all" | "active" | "archived";
  page?: number;
  page_size?: number;
}) => {
  const response = await http.get<InternalUserDirectoryResponse>(
    "/api/access-control/users",
    { params }
  );
  return response.data;
};

export const getAccessControlClients = async (params: {
  search?: string;
  status?: "all" | "Active" | "Inactive" | "On Hold";
  page?: number;
  page_size?: number;
}) => {
  const response = await http.get<ClientDirectoryResponse>(
    "/api/access-control/clients",
    { params }
  );
  return response.data;
};

export const assignInternalUserRole = async (
  userId: number | string,
  payload: AssignInternalUserAccessPayload
) => {
  const response = await http.put<{ user: ActorRoleAssignmentResult }>(
    `/api/access-control/users/${userId}/role`,
    payload
  );
  return response.data;
};

export const assignClientRole = async (
  clientId: number | string,
  payload: AssignClientAccessPayload
) => {
  const response = await http.put<{ client: ActorRoleAssignmentResult }>(
    `/api/access-control/clients/${clientId}/role`,
    payload
  );
  return response.data;
};

export const updateInternalUserModuleOverrides = async (
  userId: number | string,
  items: RoleModuleDefaultInput[]
) => {
  const response = await http.put<{ user: ActorModuleOverridesResult }>(
    `/api/access-control/users/${userId}/module-overrides`,
    { items }
  );
  return response.data;
};

export const updateClientModuleOverrides = async (
  clientId: number | string,
  items: RoleModuleDefaultInput[]
) => {
  const response = await http.put<{ client: ActorModuleOverridesResult }>(
    `/api/access-control/clients/${clientId}/module-overrides`,
    { items }
  );
  return response.data;
};

export const updateInternalUserPermissionOverrides = async (
  userId: number | string,
  items: RolePermissionDefaultInput[]
) => {
  const response = await http.put<{ user: ActorPermissionOverridesResult }>(
    `/api/access-control/users/${userId}/permission-overrides`,
    { items }
  );
  return response.data;
};

export const updateClientPermissionOverrides = async (
  clientId: number | string,
  items: RolePermissionDefaultInput[]
) => {
  const response = await http.put<{ client: ActorPermissionOverridesResult }>(
    `/api/access-control/clients/${clientId}/permission-overrides`,
    { items }
  );
  return response.data;
};
